// Pure login-redirect URL helpers. Deliberately free of MSAL / router /
// config imports (same rationale as auth.session.ts): auth.instance.ts
// instantiates the MSAL client, opens the WS client and wires Sentry at
// module load, which makes importing it directly in jest fragile. Keeping
// the "where do I land after signing in?" rules here makes them
// unit-testable in isolation (auth.redirect.test.ts).

// The login route's pathname. The single definition for every "am I on the
// login page?" check on the auth path (v2-console's router.constants.ts
// LOGIN_PAGE template must stay in the app project and so can't be imported
// from this lib).
export const LOGIN_PATHNAME = "/login"

// Search param on /login carrying the in-app path to land on after a
// successful sign-in, e.g. "/login?r=%2Fmap%2Fcadastre".
const RETURN_TO_PARAM = "r"

// Where a sign-in started from /login lands when no (valid) ?r= is present.
// v2-console's home; apps with a different home (v2-backoffice-client's "/")
// pass theirs explicitly.
const DEFAULT_START_PAGE = "/map"

// Structural subset of both window.location and react-router's Location,
// so callers and tests can pass either.
type LocationLike = {
  pathname: string
  search: string
  hash?: string
}

/**
 * Builds the "/login?r=…" URL that carries the given (current) location as
 * the return path, so completing the sign-in lands the user back where they
 * were. Includes the query string and hash (object panel `op` param, map
 * viewport, …), which the previous pathname-only implementation silently
 * dropped across a re-authentication.
 */
export function buildLoginUrlWithReturnTo(location: LocationLike): string {
  const returnTo = `${location.pathname}${location.search}${location.hash ?? ""}`
  return `${LOGIN_PATHNAME}?${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}`
}

/**
 * Resolves MSAL's `redirectStartPage` for an interactive sign-in request.
 *
 * MSAL's default (navigateToLoginRequestUrl) returns to the URL that STARTED
 * the login. That is right for a mid-app re-auth — land back on the page you
 * were on — but wrong when the login starts on /login itself: a successful
 * sign-in would land back on the login card, and the user had to press
 * "log in" a second time to actually enter the app (FUS-900). From /login we
 * resolve the requested ?r= path instead, falling back to the app's default
 * start page. Anywhere else: undefined, keeping MSAL's default behaviour.
 *
 * The ?r= value is honored only when it is an in-app absolute path, so a
 * crafted /login?r=https://evil link can't turn the IdP's post-login
 * redirect into an open redirect.
 */
export function resolveLoginRedirectStartPage(
  location: LocationLike,
  defaultStartPage = DEFAULT_START_PAGE,
): string | undefined {
  if (location.pathname !== LOGIN_PATHNAME) return undefined
  const returnTo = new URLSearchParams(location.search).get(RETURN_TO_PARAM)
  if (returnTo && isInAppPath(returnTo)) return returnTo
  return defaultStartPage
}

// In-app absolute path only: rejects full URLs ("https://evil.com"),
// protocol-relative URLs ("//evil.com") and the backslash variant
// ("/\evil.com", which browsers normalise to "//evil.com").
function isInAppPath(path: string): boolean {
  if (!path.startsWith("/")) return false
  if (path.startsWith("//")) return false
  if (path.startsWith("/\\")) return false
  return true
}
